import React, { useEffect, useMemo, useState } from "react";
import DynamicChecklistReadOnly from "./DynamicChecklistReadOnly";
import { checklistQuestionAPI } from "../services/api";
import {
  GENERAL_SR_TYPE_CODE,
  extractSrCategoriesFromInvoiceLines,
  splitChecklistResponsesBySrType,
} from "../utils/checklistSrCategoryUtils";
import { applyChildMaterialQtyToChecklistResponses } from "../utils/checklistMaterialQty";

/**
 * Read-only checklist with general questions + SR Category tabs (same layout as raise-complaint-checklist).
 */
export default function SrCategoryTabbedChecklistReadOnly({
  templateCode,
  responseRows = [],
  materialRows = [],
  childSrCode = null,
  fallbackQuestions = [],
}) {
  const [activeSrCategoryTab, setActiveSrCategoryTab] = useState(0);
  const [flatQuestions, setFlatQuestions] = useState(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  const tc = String(templateCode || "").trim();

  useEffect(() => {
    if (!tc) {
      setFlatQuestions([]);
      return undefined;
    }
    let cancelled = false;
    setHierarchyLoading(true);
    checklistQuestionAPI
      .getQuestionsHierarchy(tc)
      .then((res) => {
        const raw = Array.isArray(res)
          ? res
          : (
              res?.data ??
              res?.data?.data ??
              res?.data?.Data ??
              res?.data?.items ??
              res?.Data ??
              []
            );
        if (!cancelled) setFlatQuestions(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setFlatQuestions([]);
      })
      .finally(() => {
        if (!cancelled) setHierarchyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tc]);

  const questionTextByCode = useMemo(() => {
    const map = new Map();
    const add = (code, text) => {
      const c = String(code || "").trim();
      const t = String(text || "").trim();
      if (!c || !t) return;
      map.set(c, t);
      map.set(c.toLowerCase(), t);
    };
    (Array.isArray(flatQuestions) ? flatQuestions : []).forEach((q) =>
      add(q?.questionCode ?? q?.QuestionCode, q?.questionText ?? q?.QuestionText)
    );
    (Array.isArray(fallbackQuestions) ? fallbackQuestions : []).forEach((q) =>
      add(q?.questionCode ?? q?.QuestionCode, q?.questionText ?? q?.QuestionText)
    );
    return map;
  }, [flatQuestions, fallbackQuestions]);

  const patchedResponseRows = useMemo(
    () =>
      applyChildMaterialQtyToChecklistResponses(
        responseRows,
        materialRows,
        childSrCode,
        questionTextByCode
      ),
    [responseRows, materialRows, childSrCode, questionTextByCode]
  );

  const patchedFallbackQuestions = useMemo(() => {
    if (!Array.isArray(fallbackQuestions) || fallbackQuestions.length === 0) {
      return fallbackQuestions;
    }
    const asRows = fallbackQuestions.map((q) => ({
      QuestionText: q.questionText || q.QuestionText,
      QuestionCODE: q.questionCode || q.QuestionCode,
      SRTypeCode: q.srTypeCode || q.SRTypeCode,
      ResponseText: q.response,
    }));
    const patched = applyChildMaterialQtyToChecklistResponses(
      asRows,
      materialRows,
      childSrCode,
      questionTextByCode
    );
    return fallbackQuestions.map((q, i) => ({
      ...q,
      response: patched[i]?.ResponseText ?? q.response,
    }));
  }, [fallbackQuestions, materialRows, childSrCode, questionTextByCode]);

  const materialSrCategories = useMemo(
    () =>
      extractSrCategoriesFromInvoiceLines(materialRows, {
        childSrCode,
      }),
    [materialRows, childSrCode]
  );

  const srCategoriesForTabs = useMemo(
    () => materialSrCategories.filter((cat) => cat.code !== GENERAL_SR_TYPE_CODE),
    [materialSrCategories]
  );

  const hasMaterialSrCategories = materialSrCategories.length > 0;
  const activeSrCategoryCode =
    srCategoriesForTabs.length > 1
      ? srCategoriesForTabs[activeSrCategoryTab]?.code ?? null
      : srCategoriesForTabs.length === 1
      ? srCategoriesForTabs[0].code
      : null;

  const { generalAnswers, categoryAnswersByCode } = useMemo(
    () => splitChecklistResponsesBySrType(patchedResponseRows, flatQuestions),
    [patchedResponseRows, flatQuestions]
  );

  const legacyAnswers = useMemo(() => {
    const merged = { ...generalAnswers };
    Object.values(categoryAnswersByCode).forEach((map) => {
      Object.entries(map || {}).forEach(([q, v]) => {
        if (merged[q] == null || String(merged[q]).trim() === "") merged[q] = v;
      });
    });
    return merged;
  }, [generalAnswers, categoryAnswersByCode]);

  const sharedHierarchyProps =
    flatQuestions != null ? { flatQuestionsOverride: flatQuestions } : {};

  if (hierarchyLoading && flatQuestions == null) {
    return <p className="text-muted small mb-0 py-2">Loading checklist layout…</p>;
  }

  if (!hasMaterialSrCategories) {
    return (
      <DynamicChecklistReadOnly
        templateCode={templateCode}
        answers={legacyAnswers}
        fallbackQuestions={patchedFallbackQuestions}
        {...sharedHierarchyProps}
      />
    );
  }

  const categoryAnswers =
    activeSrCategoryCode && categoryAnswersByCode[activeSrCategoryCode]
      ? categoryAnswersByCode[activeSrCategoryCode]
      : {};

  return (
    <div className="sr-category-checklist-readonly">
      <DynamicChecklistReadOnly
        templateCode={templateCode}
        answers={generalAnswers}
        fallbackQuestions={patchedFallbackQuestions}
        srCategoryFilter="general"
        hideWhenEmpty
        {...sharedHierarchyProps}
      />

      {srCategoriesForTabs.length > 1 && (
        <div className="tabs-container checklist-tabs sr-category-tabs mb-3 mt-2">
          {srCategoriesForTabs.map((cat, idx) => (
            <button
              key={cat.code}
              type="button"
              className={`tab-button ${activeSrCategoryTab === idx ? "active" : ""}`}
              onClick={() => setActiveSrCategoryTab(idx)}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {activeSrCategoryCode && (
        <DynamicChecklistReadOnly
          templateCode={templateCode}
          answers={categoryAnswers}
          fallbackQuestions={[]}
          srCategoryFilter={activeSrCategoryCode}
          hideWhenEmpty
          {...sharedHierarchyProps}
        />
      )}
    </div>
  );
}
